import axios from 'axios'
import type {
  AutocompleteResponse,
  Card,
  CardSearchResponse,
  DeckImportRequest,
  DeckImportResponse,
  Job,
  JobOptions,
  JobStatusResponse,
  ParseListResponse,
  PreviewRequest,
  PreviewResponse,
  ResolveListRequest,
  ResolveListResponse,
} from '../types'

const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL || '/api/v1',
  headers: {
    'Content-Type': 'application/json',
  },
})

export interface BackendInfo {
  status: string
  version: string
  environment: string
  build: string
  commit: string
}

/** Lightweight health probe used to detect / wake the Render server. */
export const healthApi = {
  check: async (): Promise<boolean> => {
    try {
      await api.get('/health', { timeout: 8000 })
      return true
    } catch {
      return false
    }
  },

  /** Full health payload: backend version, build number and commit SHA. */
  info: async (): Promise<BackendInfo | null> => {
    try {
      const { data } = await api.get<BackendInfo>('/health', { timeout: 8000 })
      return data
    } catch {
      return null
    }
  },
}

export const jobsApi = {
  /**
   * Pre-generation summary ("resumen previo"): ask the backend to PLAN the
   * PDFs (solo lists vs ``_union`` merges, cards/blanks/pages, warnings)
   * WITHOUT creating any job. Read-only — safe to call on every click.
   */
  preview: async (request: PreviewRequest): Promise<PreviewResponse> => {
    const { data } = await api.post<PreviewResponse>('/jobs/preview', request)
    return data
  },

  create: async (cards: Card[], options: JobOptions = {}): Promise<Job> => {
    const { data } = await api.post<Job>('/jobs', { cards, options })
    return data
  },

  get: async (id: string): Promise<Job> => {
    const { data } = await api.get<Job>(`/jobs/${id}`)
    return data
  },

  getStatus: async (id: string): Promise<JobStatusResponse> => {
    const { data } = await api.get<JobStatusResponse>(`/jobs/${id}/status`)
    return data
  },

  cancel: async (id: string): Promise<Job> => {
    const { data } = await api.post<Job>(`/jobs/${id}/cancel`)
    return data
  },

  uploadDecklist: async (file: File): Promise<Job> => {
    const formData = new FormData()
    formData.append('file', file)
    const { data } = await api.post<Job>('/jobs/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    return data
  },

  /**
   * A7/A8: create a job from user-uploaded local images (fronts + optional
   * custom backs). ``backAssignments`` maps a front index to a back index;
   * fronts without an entry get no back. ``crop`` is 'all' | 'none' | a
   * per-front boolean array (MPC bleed crop).
   */
  createLocal: async (
    fronts: File[],
    backs: File[],
    backAssignments: Record<number, number>,
    crop: 'all' | 'none' | boolean[],
    tcg = 'mtg',
    frontsOnly = false
  ): Promise<Job> => {
    const formData = new FormData()
    fronts.forEach((f) => formData.append('fronts', f))
    backs.forEach((b) => formData.append('backs', b))
    formData.append('back_assignments', JSON.stringify(backAssignments))
    formData.append('crop', Array.isArray(crop) ? JSON.stringify(crop) : crop)
    formData.append('tcg', tcg)
    formData.append('fronts_only', String(frontsOnly))
    const { data } = await api.post<Job>('/jobs/local', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    return data
  },

  /**
   * A7/A8: create a job mixing resolved cards with user-uploaded local images.
   * ``cards`` may contain ``image_url: "local:N"`` references; ``images`` are
   * the actual files (index N). The backend saves them under the job workspace
   * and rewrites the references before creating the job.
   */
  createMixed: async (
    cards: Card[],
    images: File[],
    options: JobOptions = {}
  ): Promise<Job> => {
    const formData = new FormData()
    formData.append('cards_json', JSON.stringify(cards))
    images.forEach((img) => formData.append('images', img))
    formData.append('options_json', JSON.stringify(options))
    const { data } = await api.post<Job>('/jobs/mixed', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    return data
  },

  /** URL for the generated PDF download (F4). Open in a new tab / use as href. */
  downloadUrl: (id: string): string => {
    const base = import.meta.env.VITE_API_URL || '/api/v1'
    return `${base}/jobs/${id}/download`
  },
}

export const cardsApi = {
  /** F1: parse a raw decklist. */
  parseList: async (rawList: string): Promise<ParseListResponse> => {
    const { data } = await api.post<ParseListResponse>('/cards/parse-list', {
      raw_list: rawList,
    })
    return data
  },

  /** F2: resolve a list of cards against Scryfall. */
  resolve: async (request: ResolveListRequest): Promise<ResolveListResponse> => {
    const { data } = await api.post<ResolveListResponse>('/cards/resolve', request)
    return data
  },

  /** F2: full-text search (returns a LIST of cards, newest first). */
  search: async (query: string, limit = 20): Promise<CardSearchResponse> => {
    const { data } = await api.get<CardSearchResponse>('/cards/search', {
      params: { q: query, limit },
    })
    return data
  },

  /** F2: autocomplete card names (cheap catalog endpoint). */
  autocomplete: async (query: string, limit = 20): Promise<AutocompleteResponse> => {
    const { data } = await api.get<AutocompleteResponse>('/cards/autocomplete', {
      params: { q: query, limit },
    })
    return data
  },
}

export const decksApi = {
  /**
   * Import a deck from an external URL (Moxfield, Archidekt, Deckstats,
   * TappedOut, Manabox). The backend fetches the deck and resolves it against
   * Scryfall, returning main/side with image_url filled when found.
   *
   * ``includeTokens`` (default true) adds the deck's tokens as printable
   * cards; providers without a token list (Deckstats, ManaBox) answer with a
   * warning in ``warnings`` instead.
   *
   * On failure it throws an Error whose message is the backend's `detail`
   * (e.g. Cloudflare-block export instructions), not axios' generic
   * "Request failed with status code 422".
   */
  import: async (
    url: string,
    includeTokens: boolean = true
  ): Promise<DeckImportResponse> => {
    const body: DeckImportRequest = { url, include_tokens: includeTokens }
    try {
      const { data } = await api.post<DeckImportResponse>('/decks/import', body)
      return data
    } catch (err) {
      if (axios.isAxiosError(err)) {
        const detail = (err.response?.data as { detail?: string } | undefined)?.detail
        if (detail) throw new Error(detail)
      }
      throw err
    }
  },
}

export default api
